import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {AlertCircle, X} from 'lucide-react'
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

interface ImpersonationSession {
  sessionToken: string
  superadminId: string
  superadminEmail: string
  viewedUserId: string
  viewedUserEmail: string
  viewedUserName: string
  viewedRole: string
  enteredAt: string
}

export default function ViewingBanner() {
  const [session, setSession] = useState<ImpersonationSession | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    // Check for active impersonation session
    const sessionData = sessionStorage.getItem("impersonation_session")
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData)
        setSession(parsed)

        // Check if session expired (1 hour)
        const enteredAt = new Date(parsed.enteredAt)
        const now = new Date()
        const hoursSinceEntered = (now.getTime() - enteredAt.getTime()) / (1000 * 60 * 60)

        if (hoursSinceEntered > 1) {
          handleExit(true) // Auto-exit expired session
        }
      } catch (error) {
        console.error("Failed to parse impersonation session:", error)
      }
    }
  }, [])

  const handleExit = async (expired = false) => {
    if (!session) return

    try {
      const now = new Date().toISOString()

      // Update session record
      const sessions = await lumi.entities.ImpersonationSessions.list({
        filter: { session_token: session.sessionToken }
      })

      if (sessions.list.length > 0) {
        await lumi.entities.ImpersonationSessions.update(sessions.list[0]._id, {
          exited_at: now,
          status: expired ? "expired" : "exited"
        })
      }

      // Create audit log
      try {
        await lumi.entities.AuditLogs.create({
          actor_id: session.superadminId,
          action: "impersonation_ended",
          target_id: session.viewedUserId,
          timestamp: now,
          metadata: {
            actor_email: session.superadminEmail,
            target_email: session.viewedUserEmail,
            target_name: session.viewedUserName,
            viewed_role: session.viewedRole,
            session_token: session.sessionToken,
            reason: expired ? "Session expired (1 hour)" : "Manual exit"
          }
        })
      } catch (auditError) {
        console.warn("Audit log creation failed (non-blocking):", auditError)
      }

      // Clear session
      sessionStorage.removeItem("impersonation_session")
      setSession(null)

      if (expired) {
        toast.error("Viewing session expired (1 hour limit)")
      } else {
        toast.success("Exited viewing mode")
      }

      // Navigate back to superadmin dashboard
      navigate("/dashboard/superadmin")
    } catch (error) {
      console.error("Exit error:", error)
      toast.error("Failed to exit viewing mode")
    }
  }

  if (!session) return null

  return (
    <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className="font-bold">⚠️ Viewing Mode:</span>
            <span className="text-sm sm:text-base">
              {session.viewedUserName} ({session.viewedRole})
            </span>
            <span className="text-xs text-orange-100 hidden sm:inline">
              • All actions are audited
            </span>
          </div>
        </div>
        <button
          onClick={() => handleExit(false)}
          className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors font-semibold text-sm"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Exit Viewing</span>
          <span className="sm:hidden">Exit</span>
        </button>
      </div>
    </div>
  )
}
